import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  applyInvalidTwoFactorCodeError,
  clearInvalidTwoFactorCodeError,
  INVALID_2FA_LOGIN_MESSAGE,
  isInvalid2faLoginHttpError,
} from '@app/helpers/invalid-two-factor-login';
import { AccountService } from '@app/services/account.service';
import { AuthStore } from '@app/stores/auth.store';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

type Login2faCredentials = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type PasswordReset2faPayload = {
  identifier: string;
  token: string;
  newPassword: string;
};

type ParsedTwoFactorNavigation =
  | { kind: 'login'; credentials: Login2faCredentials }
  | { kind: 'passwordReset'; passwordReset: PasswordReset2faPayload }
  | { kind: 'invalid' };

function parseTwoFactorNavigation(raw: unknown): ParsedTwoFactorNavigation {
  if (!raw || typeof raw !== 'object') {
    return { kind: 'invalid' };
  }
  const o = raw as Record<string, unknown>;

  if ('passwordReset' in o && o['passwordReset'] && typeof o['passwordReset'] === 'object') {
    const p = o['passwordReset'] as Record<string, unknown>;
    const identifier = p['identifier'];
    const token = p['token'];
    const newPassword = p['newPassword'];
    if (
      typeof identifier === 'string' &&
      typeof token === 'string' &&
      typeof newPassword === 'string'
    ) {
      return {
        kind: 'passwordReset',
        passwordReset: { identifier, token, newPassword },
      };
    }
  }

  if ('credentials' in o && o['credentials'] && typeof o['credentials'] === 'object') {
    const c = o['credentials'] as Record<string, unknown>;
    const email = c['email'];
    const password = c['password'];
    const rememberMe = c['rememberMe'];
    if (
      typeof email === 'string' &&
      typeof password === 'string' &&
      typeof rememberMe === 'boolean'
    ) {
      return {
        kind: 'login',
        credentials: { email, password, rememberMe },
      };
    }
  }

  return { kind: 'invalid' };
}

@Component({
  selector: 'app-two-factor-auth',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, ValidatedInputComponent],
  templateUrl: './two-factor-auth.component.html',
  styleUrl: './two-factor-auth.component.scss',
})
export class TwoFactorAuthComponent {
  protected readonly parsed: ParsedTwoFactorNavigation;

  protected formGroup: FormGroup<{ code: FormControl<string> }>;
  protected submitLoading = false;
  protected errorMessage = '';

  private store = inject(AuthStore);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private accountService = inject(AccountService);
  private translate = inject(TranslateService);

  constructor(fb: FormBuilder) {
    const raw =
      this.router.getCurrentNavigation()?.extras.state ?? history.state;
    this.parsed = parseTwoFactorNavigation(raw);

    this.formGroup = fb.group({
      code: fb.nonNullable.control('', Validators.required),
    });
    this.formGroup.controls.code.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        clearInvalidTwoFactorCodeError(this.formGroup.controls.code);
      });
  }

  onSubmit(): void {
    if (this.formGroup.invalid || this.submitLoading) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const code2fa = this.formGroup.controls.code.value;

    if (this.parsed.kind === 'login') {
      this.store
        .loginWith2fa(
          {
            email: this.parsed.credentials.email,
            password: this.parsed.credentials.password,
            code2fa,
          },
          this.parsed.credentials.rememberMe,
        )
        .subscribe({
          error: (err: unknown) => {
            if (
              err instanceof Error &&
              err.message === INVALID_2FA_LOGIN_MESSAGE
            ) {
              applyInvalidTwoFactorCodeError(this.formGroup.controls.code);
            }
          },
        });
      return;
    }

    if (this.parsed.kind === 'passwordReset') {
      this.submitLoading = true;
      this.errorMessage = '';
      this.accountService
        .resetPassword({
          ...this.parsed.passwordReset,
          code2fa,
        })
        .subscribe({
          next: (response) => {
            this.submitLoading = false;
            if (!response.success) {
              this.errorMessage =
                response.errors?.join(' ') ??
                this.translate.instant('forgottenPasswordChange.resetFailed');
              return;
            }
            void this.router.navigate(['/auth/login']);
          },
          error: (err: unknown) => {
            this.submitLoading = false;
            if (err instanceof HttpErrorResponse && isInvalid2faLoginHttpError(err)) {
              applyInvalidTwoFactorCodeError(this.formGroup.controls.code);
              return;
            }
            this.errorMessage = this.formatPasswordResetHttpError(err);
          },
        });
    }
  }

  private formatPasswordResetHttpError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && Array.isArray(body.errors)) {
        const e = body.errors as string[];
        if (e.length) {
          return e.join(' ');
        }
      }
    }
    return this.translate.instant('forgottenPasswordChange.resetFailed');
  }
}
